<template>
  <div class="offcanvas-body">
    <p class="text-paragraph">
      Você pode criar gerar códigos para valores personalizados.
    </p>
    <TextInput
      required
      label="Código"
      :model="codeModel"
      type="text"
      :placeholder="`${session?.name.split(' ')[0].toUpperCase()}10`"
      @input="(event: any) => (codeModel.$model = (event.target as HTMLInputElement).value.toUpperCase())"
    />
    <TextInput
      required
      label="Desconto do código"
      :model="percentOffModel"
      type="text"
      placeholder="10%"
      @input="handlePercentOff"
      :maxLength="3"
    />

    <div
      class="d-flex align-items-center justify-content-between py-4 border-bottom"
    >
      <p class="text-paragraph text-muted mb-0">Valor atual</p>
      <p class="text-paragraph mb-0">
        {{ currentPrice }}
      </p>
    </div>
    <div
      class="d-flex align-items-center justify-content-between py-4 border-bottom"
    >
      <p class="text-paragraph text-muted mb-0">Desconto</p>
      <p class="text-paragraph mb-0">
        {{ discount }}
      </p>
    </div>
    <div
      class="d-flex align-items-center justify-content-between py-4 border-bottom"
    >
      <p class="text-paragraph text-muted mb-0">Valor final</p>
      <p class="text-paragraph mb-0">
        {{ newPrice }}
      </p>
    </div>

    <div class="border-bottom py-4">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <p class="text-subtitle mb-0">Código terá data de validade?</p>
        <SwitchInput
          :model="hasRedemptionModel"
          id="hasRedemption"
          color="secondary"
          @handleChange="handleHasRedemption"
        />
      </div>
      <p class="text-paragraph text-muted">
        Quando esta opção estiver ativa, seu código só funcionará até a data
        informada.
      </p>
      <TextInput
        v-if="hasRedemptionModel.$model"
        required
        :model="redeemByModel"
        type="text"
        :placeholder="redeemByPlaceholder"
        class="mb-0 pb-0"
        :mask="'##/##/####'"
      />
    </div>

    <div class="border-bottom py-4 mb-4">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <p class="text-subtitle mb-0">Limite de uso por paciente?</p>
        <SwitchInput
          :model="hasLimitModel"
          id="hasLimit"
          color="secondary"
          @handleChange="handleHasLimit"
        />
      </div>
      <p class="text-paragraph text-muted">
        Quando esta opção estiver ativa, este código só poderá ser utilizado por
        um mesmo paciente a quantidade de vezes informado abaixo.
      </p>
      <TextInput
        v-if="hasLimitModel.$model"
        required
        :model="maxRedemptionsCustomersModel"
        placeholder="1x"
        class="mb-0 pb-0"
      />
    </div>

    <div class="border p-4 rounded-4 d-flex mb-4">
      <img src="@/assets/img/warning_sign.svg" alt="" class="me-4" />
      <p class="text-paragraph mb-0">
        Lembramos que assim que um código é criado,
        <span class="text-subtitle">não será possível editar</span> as
        informações posteriormente.
      </p>
    </div>

    <FormButton
      label="Criar código"
      :loading="submitting"
      :disabled="!meta.valid"
      class="mb-3"
      @onClick="onSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import moment from "moment";
import { useVeeField } from "@/composables/useVeeField";
import { buildAddCouponSchema } from "@/schemas/coupon";
import { useOffcanvasStore } from "@/store/useOffcanvasStore";
import { useSessionStore } from "@/store/useSessionStore";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import { toggleOffcanvas } from "@/utils/Helper";
import { MINIMUM_APPOINTMENT_PRICE } from "@/constants";
import { useCreateCoupon } from "@/composables/useCoupons";

const { session } = useSessionStore();
const minimumPrice = MINIMUM_APPOINTMENT_PRICE;
const sessionValue = (session?.appointment_price as any) || 0;

const currentPrice = (session?.appointment_price as any)?.toCurrency?.() ?? "";
const percentOff = ref("");
const limitValue = ref("");
const redeemByPlaceholder = moment().add(90, "days").format("DD/MM/YYYY");

const { handleSubmit, meta, resetField } = useForm({
  validationSchema: toTypedSchema(
    buildAddCouponSchema(minimumPrice, sessionValue),
  ),
  initialValues: {
    code: "",
    percent_off: "",
    has_redemption: false,
    has_limit: false,
    redeem_by: "",
    max_redemptions_customers: "",
  },
});

const codeModel = useVeeField("code");
const percentOffModel = useVeeField("percent_off");
const hasRedemptionModel = useVeeField<boolean>("has_redemption");
const hasLimitModel = useVeeField<boolean>("has_limit");
const redeemByModel = useVeeField("redeem_by");
const maxRedemptionsCustomersModel = useVeeField("max_redemptions_customers");

const discount = computed(() => {
  const percent = parseInt(percentOffModel.$model);
  if (!percent) return "R$0,00";
  const disc = (percent / 100) * sessionValue;
  return disc > 0 ? (disc as any).toCurrency() : "R$0,00";
});

const newPrice = computed(() => {
  const percent = parseInt(percentOffModel.$model);
  if (!percent) return (sessionValue as any)?.toCurrency?.() ?? "";
  const disc = (percent / 100) * sessionValue;
  const vwd = sessionValue - disc;
  return vwd > 0
    ? (vwd as any).toCurrency()
    : (sessionValue as any)?.toCurrency?.() ?? "";
});

const { mutateAsync: createCoupon, isPending: submitting } = useCreateCoupon();

function handlePercentOff(event: any) {
  let text = event.target.value;
  let t = (text as any).clear();
  if (t && t.length > 2) return;
  if (percentOff.value.length > text.length) {
    t = text.substring(0, text.length - 1);
  }
  percentOff.value = (t as any).toPercentage();
  percentOffModel.$model = (t as any).toPercentage();
}

function handleLimit(event: any) {
  let text = event.target.value;
  let t = text.replace("x", "");
  if (t && t.length > 3) return;
  if (limitValue.value.length > text.length) {
    t = text.substring(0, text.length - 1);
  }
  limitValue.value = (t as any).toQuantity();
  maxRedemptionsCustomersModel.$model = (t as any).toQuantity();
}

const onSubmit = handleSubmit(async (values) => {
  try {
    await createCoupon({
      code: values.code,
      percent_off: values.percent_off,
      ...(values.has_redemption && { redeem_by: values.redeem_by }),
      ...(values.has_limit && {
        max_redemptions_customers: values.max_redemptions_customers,
      }),
    });
    toggleOffcanvas("offcanvas_views");
    useOffcanvasStore().clear();
    EventBus.$emit(EventBusNames.SHOW_TOAST, {
      title: "Sucesso",
      msg: "Código criado com sucesso",
    });
  } catch (error: any) {
    alert(error.message || "Ocorreu um erro no servidor");
  }
});

function handleHasRedemption(event: any) {
  if (!event.target.checked) {
    resetField("redeem_by", { value: "" });
  }
}

function handleHasLimit(event: any) {
  if (!event.target.checked) {
    resetField("max_redemptions_customers", { value: "" });
  }
}
</script>
